import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { SubSink } from 'subsink';

import { SegmentDiscountResponse } from '@app/api/models';
import { AuthenticationSelectors } from '@authentication/store/authentication.slice';
import { cancellationMockDate } from '@shared/constants/cancellation-mock-date';
import { dateToParam } from '@shared/helpers/date-to-param.helper';
import { DialogService } from '@shared/services/dialog.service';

import {
  SegmentDiscountsActions,
  SegmentDiscountsSelectors,
  availableMerchantsForSegmentDiscount,
} from '../store/segment-discounts.slice';

@Component({
  selector: 'app-segment-discounts',
  templateUrl: './segment-discounts.component.html',
  styleUrls: ['./segment-discounts.component.scss'],
})
export class SegmentDiscountsComponent implements OnInit {
  availableMerchantsForSegmentDiscount = availableMerchantsForSegmentDiscount;
  subs = new SubSink();

  selectedSiteId?: string;
  selectedMerchantType?: string;
  cancellationMockDate = cancellationMockDate;

  today = new Date();
  minStartDate = new Date();

  formGroup: FormGroup = new FormGroup({
    discountValue: new FormControl(null, [
      Validators.required,
      Validators.max(99),
      Validators.min(0.0001),
    ]),
    startDate: new FormControl(new Date(), Validators.required),
    endDate: new FormControl(''),
  });

  discountGroups$ = this.store.select(
    SegmentDiscountsSelectors.selectSegmentDiscountGroups
  );
  editRowIndex$ = this.store.select(
    SegmentDiscountsSelectors.selectEditRowIndex
  );

  ngOnInit(): void {
    this.subs.sink = this.store
      .select(AuthenticationSelectors.selectUserInfo)
      .subscribe((userInfo) => {
        if (
          this.selectedSiteId !==
            userInfo.attributes?.['custom:selectedSiteId'] ||
          this.selectedMerchantType !==
            userInfo.attributes?.['custom:selectedMerchantType']
        ) {
          this.selectedSiteId = userInfo.attributes?.['custom:selectedSiteId'];
          this.selectedMerchantType =
            userInfo.attributes?.['custom:selectedMerchantType'];
          this.store.dispatch(
            SegmentDiscountsActions.loadSegmentDiscounts.trigger()
          );
        }
      });
  }

  editDiscount(
    merchantIndex: number,
    productIndex: number,
    discount: SegmentDiscountResponse
  ) {
    this.formGroup.reset();
    this.formGroup.patchValue({
      discountValue: discount.discountValue,
      startDate: discount.startDate,
      endDate:
        discount.endDate === cancellationMockDate ? '' : discount.endDate,
    });
    this.minStartDate =
      discount.startDate === '' ? this.today : new Date(discount.startDate);

    this.store.dispatch(
      SegmentDiscountsActions.setEditRowIndex.trigger({
        index: `${merchantIndex}|${productIndex}`,
      })
    );
  }

  saveDiscount(discount: SegmentDiscountResponse) {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.valid) {
      let editedDiscount = { ...discount };
      editedDiscount.discountValue = this.formGroup.value.discountValue;
      editedDiscount.startDate = dateToParam(this.formGroup.value.startDate);
      editedDiscount.endDate = this.formGroup.value.endDate
        ? dateToParam(this.formGroup.value.endDate)
        : dateToParam(new Date(cancellationMockDate));
      if (discount.id) {
        this.store.dispatch(
          SegmentDiscountsActions.updateSegmentDiscount.trigger({
            discount: editedDiscount,
          })
        );
      } else {
        this.store.dispatch(
          SegmentDiscountsActions.createSegmentDiscount.trigger({
            discount: editedDiscount,
          })
        );
      }
    }
  }

  cancelEdit() {
    this.store.dispatch(
      SegmentDiscountsActions.setEditRowIndex.trigger({
        index: '',
      })
    );
    this.formGroup.reset();
  }
  deleteDiscount(discount: SegmentDiscountResponse) {
    this.dialogService.confirm(
      'DISCOUNTS.DELETE_CONFIRM_TITLE',
      'DISCOUNTS.DELETE_CONFIRM_MESSAGE',
      () => {
        this.store.dispatch(
          SegmentDiscountsActions.deleteSegmentDiscount.trigger({
            discounts: [discount],
          })
        );
      }
    );
  }

  constructor(private store: Store, private dialogService: DialogService) {}
}
